import React, { useEffect, useMemo, useState } from 'react';

import { Flex } from '@reckon-web/flex';
import { Toggle } from '@reckon-web/toggle';
import { FieldLabel } from '@reckon-web/field';
import { Button } from '@reckon-web/button';
import { Field } from '@reckon-web/field';
import { SearchInput } from '@reckon-web/search-input';
import { Inline } from '@reckon-web/inline';
import { useTheme } from '@reckon-web/theme';
import { makeId } from '@reckon-web/utils';
import { ConfigMenu, ConfigMenuGroup } from '@reckon-web/config-menu';

import { ensureArray } from '../../../utils/array-utils';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../../../data/useGetMembersList';
import { useSearchClients } from '../../../data/useSearchClients';
import { useSearchEntities } from '../../../data/useSearchEntities';
import { useSearchFamilyGroups } from '../../../data/useSearchFamilyGroups';
import {
  JobAutoCompleteResult,
  useJobAutoComplete,
} from '../../../hooks/use-job-auto-complete';
import {
  INACTIVE_USER_VALUE,
  UNALLOCATED_USER_VALUE,
  taskStatuses,
} from '../../../globals';
import {
  TaskStatusBadge,
  getTaskStatusLabel,
} from '../../common/task-status-badge';
import { DateRangePicker } from '../../common/date-range-picker';
import { MultiFilterMenu, Option } from '../../common/multi-filter-menu';
import {
  VisibleFilters as VisibleFiltersType,
  useVisibleFilters,
} from '../../common/visible-filters';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import { TasksFilter } from '../filter-helpers';
import { maybeify, stringCompare } from '../../../utils/sort';
import { DisabledElementTooltip } from '../../common/disabled-element-tooltip';
import { useGetClientsByIds } from '../../../data/useGetClientsById';
import { useGetEntitiesByIds } from '../../../data/useGetEntitiesByIds';
import { useGetFamilyGroupsByIds } from '../../../data/useGetFamilyGroupsByIds';
import {
  processData as processGetJobLinkDetails,
  useGetJobLinkDetails,
} from '../../../data/useGetJobLinkDetails';
import {
  processData as processGetClientLite,
  useGetClientLite,
} from '../../../data/useGetClientLite';
import { useGetVisibleJobStatuses } from '../../../hooks/use-get-visible-job-statuses';
import type { GetMyDetailsQueryUser } from '../../../data/useGetMyDetails';
import { JobStatusEnum } from '../../job/types/job-status-enum';

function jobAutoCompleteToMenuOption(j: JobAutoCompleteResult) {
  return {
    value: j.job.cloudId || '',
    label: j.job.name + (j.client ? ` (${j.client.name})` : ''),
  };
}

function userToMenuOption(user: GetMemberListItem) {
  return {
    label: user.fullName || '',
    value: user.cloudId || '',
    render: (
      <NamedUserAvatar user={user} avatarSize="xsmall" textSize="small" />
    ),
  };
}
function fakeUserToMenuOption(item: Option<string>) {
  return {
    ...item,
    render: (
      <NamedUserAvatar
        user={null}
        nameOverride={item.label}
        avatarSize="xsmall"
        textSize="small"
      />
    ),
  };
}

// not a huge fan of exporting this, but I don't know where else to put it
// helpers maybe?
export function filterUserList(
  users: GetMyDetailsQueryUser[] | undefined | null,
  searchTerm: string | undefined
) {
  if (!users) {
    return [];
  }

  // could keep these as separate useMemos, but that could be a bit excessive
  const normalUsers = users.filter((user) => user.licensed && user.active);
  const inactiveUsers = users.filter((user) => !user.licensed || !user.active);

  // make up something we can QUICKLY use to test the strings in a case insenstivie manner
  const search = searchTerm
    ? new RegExp(searchTerm, 'i')
    : { test: (val: string) => true };

  // find the matching users, take the first 20
  const matchingNormalusers = normalUsers
    .filter((x) => search.test(x.fullName || ''))
    .sort((a, b) => maybeify(stringCompare)(a.fullName, b.fullName))
    .slice(0, 20);

  // if they've not selected inactive, find the matching inactive users, take the first 20
  // if they've selected inactive, we aren't showing any users at all
  const matchingInactiveUsers = inactiveUsers
    .filter((x) => search.test(x.fullName || ''))
    .sort((a, b) => maybeify(stringCompare)(a.fullName, b.fullName))
    .slice(0, 20);

  // order of the list goes
  //   unallocated selector
  //   normal users
  //   inactive users selector
  //   inactive users
  const result = [
    fakeUserToMenuOption({
      label: 'Unallocated',
      value: UNALLOCATED_USER_VALUE,
    }),
    ...matchingNormalusers.map(userToMenuOption),
    fakeUserToMenuOption({
      label: 'All inactive users',
      value: INACTIVE_USER_VALUE,
    }),
    ...matchingInactiveUsers.map(userToMenuOption),
  ];
  // then take the first 22 of them
  // 2 for the special items, then max of 20 other users shown
  // if there's more than 20 matching normal users, this means we'll have inactive as the last item
  return result.slice(0, 22);
}

const statusOptions = taskStatuses.map(({ id }) => ({
  label: getTaskStatusLabel(id),
  value: id,
  render: <TaskStatusBadge status={id} />,
}));

const tasksVisibleFilters: {
  label: string;
  value: keyof VisibleFiltersType;
  disabled: boolean;
}[] = [
  { label: 'Family group', value: 'allFamilyGroup', disabled: false },
  { label: 'Task completed date', value: 'allCompletedDate', disabled: false },
];

export const Filter: React.FC<{
  filter: TasksFilter;
  setFilter: (value: Partial<TasksFilter>) => void;
  hasFiltersChanged: boolean;
  clearFilter: () => void;
  saveUserFilter: () => void;
  testId?: string;
}> = ({
  filter,
  setFilter,
  hasFiltersChanged,
  clearFilter,
  saveUserFilter,
  testId = `tasks__filter`,
}) => {
  const theme = useTheme();
  const [visibleFilters, setVisibleFilters] = useVisibleFilters();

  const users = useGetMembersList();
  const searchJobs = useJobAutoComplete();
  const getJobLinkDetails = useGetJobLinkDetails({ skip: true });
  const getClientLite = useGetClientLite({ skip: true });

  const visibleJobStatuses = useGetVisibleJobStatuses();
  const IdsOfStatusesThatAreNotClosed = useMemo(() => {
    return visibleJobStatuses.data
      ?.filter((x) => x.status !== JobStatusEnum.valuesEnum.Closed)
      .map((x) => x.cloudId);
  }, [visibleJobStatuses]);

  const clients = useSearchClients({
    variables: {
      searchTerm: '',
      take: 20,
      includeNonClients: false,
    },
    skip: true,
  });
  const getClientsByIds = useGetClientsByIds({ skip: true });
  const entities = useSearchEntities({
    variables: {
      searchTerm: '',
      take: 20,
      includeNonEntities: false,
    },
    skip: true,
  });
  const getEntitiesByIds = useGetEntitiesByIds({ skip: true });
  const familyGroups = useSearchFamilyGroups({
    variables: {
      searchTerm: '',
      take: 20,
      includeNonFamilyGroups: false,
    },
    skip: true,
  });
  const getFamilyGroupsByIds = useGetFamilyGroupsByIds({ skip: true });

  const [clientsOptions, setClientsOptions] = useState<Option<string>[]>([]);
  const [isSearchingClients, setIsSearchingClients] = useState<boolean>(false);
  const doSearchClients = async (searchTerm?: string) => {
    setIsSearchingClients(true);
    const results = (
      await clients.refetch({ searchTerm })
    ).data?.searchClients?.map(({ cloudId, name }) => ({
      label: name || '',
      value: cloudId,
    }));
    setClientsOptions(results || []);
    setIsSearchingClients(false);
  };
  const doLoadClients = async (ids: string[]) => {
    return (
      (
        await getClientsByIds.refetch({ cloudIds: ids })
      ).data?.getClientsByIds?.map(({ cloudId, name }) => ({
        label: name || '',
        value: cloudId,
      })) || []
    );
  };
  const [entitiesOptions, setEntitiesOptions] = useState<Option<string>[]>([]);
  const [isSearchingEntities, setIsSearchingEntities] =
    useState<boolean>(false);
  const doSearchEntities = async (searchTerm?: string) => {
    setIsSearchingEntities(true);
    const results = (
      await entities.refetch({ searchTerm })
    ).data?.searchEntities?.map(({ cloudId, name }) => ({
      label: name || '',
      value: cloudId,
    }));
    setEntitiesOptions(results || []);
    setIsSearchingEntities(false);
  };
  const doLoadEntities = async (ids: string[]) => {
    return (
      (
        await getEntitiesByIds.refetch({ cloudIds: ids })
      ).data?.getEntitiesByIds?.map(({ cloudId, name }) => ({
        label: name || '',
        value: cloudId,
      })) || []
    );
  };

  const [jobsOptions, setJobsOptions] = useState<Option<string>[]>([]);
  const [isSearchingJobs, setIsSearchingJobs] = useState<boolean>(false);
  const doSearchJobs = async (searchTerm?: string) => {
    setIsSearchingJobs(true);
    const results =
      (
        await searchJobs(searchTerm || '', IdsOfStatusesThatAreNotClosed)
      ).data?.map(jobAutoCompleteToMenuOption) || [];
    setJobsOptions(results || []);
    setIsSearchingJobs(false);
  };

  // this is only used when applying a filter. As such, it's not a high priority for performance improvements
  const doLoadJobs = async (ids: string[]) => {
    // TODO getJobsByIds now has security, this can be used
    // this one will be really weird. There's a getJobsByIds on core graphql, but it doesn't have security set
    // so we can't use it. And it also doesn't load the client we need
    // instead, we'll the ones that power the job link, they'll give us a similar enough to the job autocomplete
    // we can't use joblink hook directly, as it doesn't support refetch, which we need
    // Could write a specific get-job-link-awaitable or some nonesense, but this'll work
    const results: Option<string>[] = [];
    for (const id of ids) {
      const jobResult = await getJobLinkDetails.refetch({ cloudId: id });
      const job = processGetJobLinkDetails(jobResult.data);
      if (job?.clientId) {
        const clientResult = await getClientLite.refetch({
          cloudId: job.clientId,
        });
        const client = processGetClientLite(clientResult.data);
        if (client) {
          // hooray, we have a job and a client. Add them to the results
          results.push(jobAutoCompleteToMenuOption({ job, client }));
        }
      }
    }
    return results;
  };

  const [familyGroupsOptions, setFamilyGroupsOptions] = useState<
    Option<string>[]
  >([]);
  const [isSearchingFamilyGroups, setIsSearchingFamilyGroups] =
    useState<boolean>(false);
  const doSearchFamilyGroups = async (searchTerm?: string) => {
    setIsSearchingFamilyGroups(true);
    const results = (
      await familyGroups.refetch({ searchTerm })
    ).data?.searchFamilyGroups?.map(({ cloudId, name }) => ({
      label: name || '',
      value: cloudId,
    }));
    setFamilyGroupsOptions(results || []);
    setIsSearchingFamilyGroups(false);
  };
  const doLoadFamilyGroups = async (ids: string[]) => {
    return (
      (
        await getFamilyGroupsByIds.refetch({ cloudIds: ids })
      ).data?.getFamilyGroupsByIds?.map(({ cloudId, name }) => ({
        label: name || '',
        value: cloudId,
      })) || []
    );
  };

  // user dropdowns are a bit different to the other dropdowns. It has special sorting and items
  // so, we'll do this with a useMemo that rebuilds when users load or search term changes
  const [staffSearchTerm, setStaffSearchTerm] = useState<string>('');
  const staffOptions = useMemo(() => {
    return filterUserList(users.data, staffSearchTerm);
  }, [users.data, staffSearchTerm]);

  // kick off the searches for the things, so that the lists don't start off empty. This is totally optional
  // and probably utterly pointless, but the old app always started off with populated lists
  useEffect(() => {
    doSearchClients('');
    doSearchEntities('');
    doSearchFamilyGroups('');
    doSearchJobs('');
    // yeah, nah, we don't want this effect to fire when those functions change. They're not stable
    // and they're also not easy to make stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (users.error || clients.error || entities.error || familyGroups.error)
    return <div>Error!</div>;

  return (
    <Flex gap="medium" alignItems="flex-start" data={{ testid: testId }}>
      <Inline alignY="top" gap="small">
        <Field label="Filter" labelVisible={false}>
          <SearchInput
            placeholder="Filter..."
            size="small"
            onChange={(e) => setFilter({ text: e.target.value })}
            onClear={() => setFilter({ text: '' })}
            value={filter.text || ''}
            css={{ width: '128px' }}
            data-testid={makeId(testId, 'search')}
          />
        </Field>
        <Flex
          gap="xsmall"
          paddingX="xsmall"
          alignItems="center"
          height={theme.sizing.small}
        >
          <Toggle
            size="small"
            id="my-work-toggle"
            checked={Boolean(filter.myWork)}
            onChange={(myWork) => setFilter({ myWork })}
            data-testid={makeId(testId, 'my-work-toggle')}
          />
          <FieldLabel
            htmlFor="my-work-toggle"
            css={{ lineHeight: theme.typography.leading.tighter }}
            data-testid={makeId(testId, 'my-work-label')}
          >
            My work
          </FieldLabel>
        </Flex>
        <Flex
          gap="xsmall"
          paddingX="xsmall"
          alignItems="center"
          height={theme.sizing.small}
        >
          <Toggle
            size="small"
            id="overdue-toggle"
            checked={Boolean(filter.dueDateOverdue)}
            onChange={(dueDateOverdue) => setFilter({ dueDateOverdue })}
            data-testid={makeId(testId, 'overdue-toggle')}
          />
          <FieldLabel
            htmlFor="overdue-toggle"
            css={{ lineHeight: theme.typography.leading.tighter }}
            data-testid={makeId(testId, 'overdue-label')}
          >
            Overdue
          </FieldLabel>
        </Flex>
        <MultiFilterMenu
          label="Debtor"
          options={clientsOptions}
          values={ensureArray(filter.clients)}
          isLoading={isSearchingClients}
          onInputChanged={doSearchClients}
          setValues={(clients) => setFilter({ clients })}
          loadOptions={doLoadClients}
          testId={makeId(testId, 'debtor')}
        />
        <MultiFilterMenu
          label="Client"
          options={entitiesOptions}
          values={ensureArray(filter.entities)}
          isLoading={isSearchingEntities}
          onInputChanged={doSearchEntities}
          setValues={(entities) => setFilter({ entities })}
          loadOptions={doLoadEntities}
          testId={makeId(testId, 'client')}
        />
        <MultiFilterMenu
          label="Job"
          options={jobsOptions}
          values={ensureArray(filter.jobs)}
          isLoading={isSearchingJobs}
          onInputChanged={doSearchJobs}
          setValues={(jobs) => setFilter({ jobs })}
          loadOptions={doLoadJobs}
          testId={makeId(testId, 'job')}
        />
        <DisabledElementTooltip
          label="Staff select dropdown"
          content="Cannot select individual users when My Work is on"
          childDisabled={Boolean(filter.myWork)}
        >
          <MultiFilterMenu
            label="Staff"
            options={staffOptions}
            values={ensureArray(filter.staff)}
            onInputChanged={(inputValue?: string) =>
              setStaffSearchTerm(inputValue || '')
            }
            setValues={(staff) => setFilter({ staff })}
            disabled={Boolean(filter.myWork)}
            testId={makeId(testId, 'staff')}
          />
        </DisabledElementTooltip>
        <MultiFilterMenu
          label="Status"
          options={statusOptions}
          moveSelectionsToTop={false}
          values={filter.statuses || []}
          setValues={(statuses) => setFilter({ statuses })}
          testId={makeId(testId, 'status')}
        />
        <DisabledElementTooltip
          label="Due date range picker"
          content="Cannot select date when overdue is on"
          childDisabled={Boolean(filter.dueDateOverdue)}
        >
          <DateRangePicker
            label="Due"
            value={{ start: filter.dueDateBegin, end: filter.dueDateEnd }}
            onChange={({ start: dueDateBegin, end: dueDateEnd }) =>
              setFilter({ dueDateBegin, dueDateEnd })
            }
            disabled={Boolean(filter.dueDateOverdue)}
            testId={makeId(testId, 'due')}
          />
        </DisabledElementTooltip>
        {visibleFilters.allFamilyGroup && (
          <MultiFilterMenu
            label="Family group"
            options={familyGroupsOptions}
            values={ensureArray(filter.familyGroupIds)}
            isLoading={isSearchingFamilyGroups}
            onInputChanged={doSearchFamilyGroups}
            setValues={(familyGroupIds) => setFilter({ familyGroupIds })}
            loadOptions={doLoadFamilyGroups}
            test-id={makeId(testId, 'family-group')}
          />
        )}
        {visibleFilters.allCompletedDate && (
          <DateRangePicker
            label="Completed"
            value={{
              start: filter.completedDateBegin,
              end: filter.completedDateEnd,
            }}
            onChange={({ start: completedDateBegin, end: completedDateEnd }) =>
              setFilter({ completedDateBegin, completedDateEnd })
            }
            testId={makeId(testId, 'completed')}
          />
        )}
        {hasFiltersChanged && (
          <Flex alignItems="center">
            <Button
              size="small"
              weight="none"
              tone="passive"
              label="Clear"
              onClick={clearFilter}
              data-testid={makeId(testId, 'clear')}
            />
            <Button
              size="small"
              weight="none"
              tone="passive"
              label="Save&hellip;"
              onClick={saveUserFilter}
              data-testid={makeId(testId, 'save')}
            />
          </Flex>
        )}
        <ConfigMenu trigger="More">
          <ConfigMenuGroup
            title="Task filters"
            onChange={(x) =>
              setVisibleFilters((prev: VisibleFiltersType) => ({
                ...prev,
                ...Object.fromEntries(
                  tasksVisibleFilters.map<[keyof VisibleFiltersType, boolean]>(
                    ({ value, disabled }) => [
                      value,
                      disabled || x.includes(value),
                    ]
                  )
                ),
              }))
            }
            options={tasksVisibleFilters}
            value={(
              Object.entries(visibleFilters) as [
                keyof VisibleFiltersType,
                boolean,
              ][]
            )
              .filter(([, v]) => v)
              .map(([k]) => k)}
            data-testid={makeId(testId, 'more-filters-tasks')}
          />
        </ConfigMenu>
      </Inline>
    </Flex>
  );
};
